import { money } from "./unitStats";

const day = (v) => {
  if (!v) return "—";
  const d = new Date(String(v).slice(0, 10));
  if (!Number.isFinite(d.getTime())) return String(v);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

const place = (u) => (u.building_name && u.building_name !== u.property_name ? `${u.property_name} · ${u.building_name}` : u.property_name);
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "Amy Renter, Bo" from Oct 1 · $1,450 */
const Incoming = ({ lease }) => {
  if (!lease) return <span className="avp-muted">Signed lease</span>;
  const names = lease.tenants?.length ? lease.tenants.join(", ") : "Signed lease";
  return (
    <>
      <span className="avp-strong">{names}</span>
      <span className="avp-muted"> from {day(lease.start_date)}</span>
    </>
  );
};

/** New rent against target: green at/above, amber below. */
const RentVsTarget = ({ rent, target }) => {
  if (rent === null || rent === undefined) return <span className="avp-muted">—</span>;
  if (!target) return money(rent);
  const diff = rent - target;
  return (
    <>
      {money(rent)}
      <span className={diff >= 0 ? "avp-up" : "avp-down"}> {diff >= 0 ? "+" : "−"}{money(Math.abs(diff))}</span>
    </>
  );
};

/** Renewal (same resident stays) or New (a new resident moves in). */
const KindTag = ({ next }) =>
  next.renewal
    ? <span className={`avp-tag ${next.signed ? "avp-tag--renewed" : "avp-tag--pending"}`}>Renewal</span>
    : <span className="avp-tag avp-tag--new">New</span>;

/**
 * The lease lined up after the current one: a signed one counts, one still out for
 * signatures is shown but not counted.
 */
const NextLease = ({ next, none = <span className="avp-muted">Not rented yet</span> }) => {
  if (!next) return none;
  if (next.signed) {
    return (
      <>
        <span className="avp-up">✓ </span>
        <KindTag next={next} />{" "}
        <Incoming lease={next.lease} />
      </>
    );
  }
  return (
    <>
      <KindTag next={next} />{" "}
      <Incoming lease={next.lease} />
      <span className="avp-down"> · awaiting signatures</span>
    </>
  );
};

/** "Nov 1, 2026 → Oct 31, 2027" */
const Term = ({ lease }) => (lease?.start_date ? <>{day(lease.start_date)}{lease.end_date ? ` → ${day(lease.end_date)}` : ""}</> : <span className="avp-muted">—</span>);

const Now = ({ u }) =>
  u.where === "VACANT" ? <span className="avp-tag avp-tag--vacant">Vacant now</span> : (
    <span>
      <span className="avp-strong">{u.tenant}</span>
      <span className="avp-muted"> ends {day(u.lease_end)}</span>
    </span>
  );

const UnitCell = ({ u }) => (
  <td>
    <span className="avp-strong">{u.unit_no}</span>
    <div className="avp-muted avp-small">{place(u)}</div>
  </td>
);

const dash = (n) => n || "—";

/** What is still to renew or rent — red while anything is left (owner, 2026-10-02). */
const Open = ({ n }) => (n ? <span className="avp-open">{n}</span> : "—");

/**
 * What each tile's popover shows. `vacant` — units empty today (Occupancy / Current Vacancy).
 * `ending` — leases ending in the window and what follows them (Renewals). `rented` —
 * of those ending leases, the next lease signed (renewal or new) or still out for
 * signatures (Renewed / Rented / Pending).
 */
const VIEWS = {
  vacant: {
    aria: "Vacancy detail",
    pick: (u) => u.where === "VACANT",
    title: (s) => `Vacant now · ${plural(s.vacant, "unit")}`,
    sub: (s, rentedInfo) => (rentedInfo ? `${s.rentedVacant} already rented · ${s.vacant - s.rentedVacant} still to rent` : `${s.occupancy}% occupied`),
    groupHas: (g) => g.vacant > 0,
    groupCols: (rentedInfo) => ["Vacant", ...(rentedInfo ? ["Rented", "Pending", "To rent"] : [])],
    groupCells: (g, rentedInfo) => [
      g.vacant,
      ...(rentedInfo ? [dash(g.rentedVacant), dash(g.upcoming - g.upcomingEnding), <Open n={g.vacant - g.rentedVacant} />] : []),
    ],
    section: "Vacant units",
    empty: "No vacant units.",
  },
  ending: {
    aria: "Renewals detail",
    pick: (u) => u.where === "ENDING",
    title: (s) => `Leases ending ${s.window.startLabel} – ${s.window.endLabel} · ${s.expiring}`,
    sub: (s, rentedInfo) =>
      rentedInfo ? (
        <>
          {s.renewedExpiring} renewed · {s.rentedExpiring - s.renewedExpiring} re-rented to a new resident · {s.upcomingExpiring} pending ·{" "}
          <span className="avp-open">{s.openExpiring} still open</span>
        </>
      ) : (
        `${plural(s.expiring, "lease")} ending in the next ${s.window.daysToEnd} days`
      ),
    groupHas: (g) => g.ending > 0,
    groupCols: (rentedInfo) => ["Ending", ...(rentedInfo ? ["Renewed", "New lease", "Pending", "Open"] : [])],
    groupCells: (g, rentedInfo) => [
      g.ending,
      ...(rentedInfo ? [dash(g.renewed), dash(g.rentedEnding - g.renewed), dash(g.upcomingEnding), <Open n={g.ending - g.rentedEnding} />] : []),
    ],
    section: "Leases ending",
    empty: (s) => `No leases ending by ${s.window.endLabel}.`,
  },
  rented: {
    aria: "Renewed and rented detail",
    // Leases ending in the window only (owner, 2026-10-02): adds up with the Renewals tile.
    pick: (u) => u.where === "ENDING" && (u.rented || u.upcoming),
    title: (s) => `Renewed / Rented / Pending · ${s.renewedExpiring} / ${s.newRented} / ${s.upcomingExpiring}`,
    sub: (s) => `${plural(s.rentedExpiring, "signed lease")} for leases ending by ${s.window.endLabel} · ${s.upcomingExpiring} awaiting signatures`,
    groupHas: (g) => g.rentedEnding > 0 || g.upcomingEnding > 0,
    groupCols: () => ["Renewed", "Rented", "Signed", "Pending"],
    groupCells: (g) => [dash(g.renewed), dash(g.rentedEnding - g.renewed), dash(g.rentedEnding), dash(g.upcomingEnding)],
    section: "Signed and pending leases",
    empty: "No signed or pending leases yet.",
  },
};

/**
 * The detail behind the Occupancy / Current Vacancy, Renewals and Renewed / Rented / Pending
 * tiles: a per-building summary with its total, the units behind the number, then
 * (Renewals) the renewals on file and the past-end leases the counts leave out.
 * `inline` lays it out in the page (the Renewals report) instead of floating it.
 */
const AvailabilityPopover = ({ kind, detail, stats, hasRentedInfo, inline = false }) => {
  const view = VIEWS[kind] || VIEWS.ending;
  const units = detail.units.filter(view.pick);
  const groups = detail.groups.filter(view.groupHas);
  const showPastEnd = kind !== "vacant";
  const pastEnd = kind === "rented" ? detail.pastEnd.filter((u) => u.rented) : detail.pastEnd;
  const cols = view.groupCols(hasRentedInfo);
  const cells = (g) => view.groupCells(g, hasRentedInfo).map((v, i) => <td key={cols[i]} className="avp-num">{v}</td>);

  return (
    <div className={`avp ${inline ? "avp--inline" : ""}`.trim()} role={inline ? "region" : "dialog"} aria-label={view.aria}>
      <div className="avp-head">
        <div className="avp-title">{view.title(stats)}</div>
        <div className="avp-sub">{view.sub(stats, hasRentedInfo)}</div>
        {kind === "ending" && hasRentedInfo && stats.upcomingExpiring > 0 && (
          <div className="avp-sub">
            {stats.upcomingExpiring} pending = next lease awaiting signatures — counted once signed
          </div>
        )}
      </div>

      {groups.length > 0 && (
        <table className="avp-table avp-table--summary">
          <thead>
            <tr>
              <th>Property</th>
              {cols.map((c) => <th key={c} className="avp-num">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.key}>
                <td>{place(g)}</td>
                {cells(g)}
              </tr>
            ))}
          </tbody>
          {groups.length > 1 && (
            <tfoot>
              <tr className="avp-total" data-testid="avp-total">
                <td>Total · {plural(groups.length, "building")}</td>
                {cells(detail.total)}
              </tr>
            </tfoot>
          )}
        </table>
      )}

      <div className="avp-section">{view.section}</div>
      {units.length === 0 ? (
        <div className="avp-empty">{typeof view.empty === "function" ? view.empty(stats) : view.empty}</div>
      ) : (
        <table className="avp-table">
          <thead>
            <tr>
              <th>Unit</th>
              {kind !== "vacant" && <th>Now</th>}
              {kind === "rented" ? (
                <>
                  <th>Incoming</th>
                  <th className="avp-num">New rent vs target</th>
                </>
              ) : (
                <>
                  <th className="avp-num">Target</th>
                  {hasRentedInfo && <th>Next lease</th>}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {units.map((u) => (
              <tr key={u.key}>
                <UnitCell u={u} />
                {kind !== "vacant" && <td><Now u={u} /></td>}
                {kind === "rented" ? (
                  <>
                    <td><NextLease next={u.next} none={<Incoming lease={u.incoming} />} /></td>
                    <td className="avp-num"><RentVsTarget rent={(u.incoming || u.next?.lease)?.basic_rent} target={u.target_rent} /></td>
                  </>
                ) : (
                  <>
                    <td className="avp-num">{u.target_rent ? money(u.target_rent) : "—"}</td>
                    {hasRentedInfo && <td><NextLease next={u.next} /></td>}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Lease ending, renewal already in the system (owner, 2026-09-30): the same resident
          stays — counted as renewed once it is signed. */}
      {kind === "ending" && hasRentedInfo && detail.renewals.length > 0 && (
        <>
          <div className="avp-section avp-section--renewals">
            Renewals on file · {plural(detail.renewals.length, "unit")}
            <span className="avp-muted"> — lease ending, renewal already in the system</span>
          </div>
          <table className="avp-table">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Resident</th>
                <th>Renewal term</th>
                <th className="avp-num">Rent now → renewal</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {detail.renewals.map((u) => (
                <tr key={`renewal-${u.key}`}>
                  <UnitCell u={u} />
                  <td>
                    <span className="avp-strong">{u.tenant}</span>
                    <div className="avp-muted avp-small">{u.where === "PAST_END" ? "ended" : "ends"} {day(u.lease_end)}</div>
                  </td>
                  <td><Term lease={u.next.lease} /></td>
                  <td className="avp-num">
                    {u.basic_rent ? money(u.basic_rent) : "—"} → <RentVsTarget rent={u.next.lease?.basic_rent} target={u.target_rent} />
                  </td>
                  <td>
                    {u.next.signed
                      ? <span className="avp-tag avp-tag--renewed">Signed · renewed</span>
                      : <span className="avp-tag avp-tag--pending">Awaiting signatures</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {showPastEnd && pastEnd.length > 0 && (
        <>
          <div className="avp-section avp-section--past">
            Past lease end · {plural(pastEnd.length, "unit")}
            {kind === "ending" && hasRentedInfo && ` (${pastEnd.filter((u) => u.rented).length} with a signed lease waiting)`}
            <span className="avp-muted"> — not counted above; tenant still listed</span>
          </div>
          <table className="avp-table">
            <tbody>
              {pastEnd.map((u) => (
                <tr key={u.key}>
                  <UnitCell u={u} />
                  <td>
                    {u.tenant}
                    <span className="avp-down"> ended {day(u.lease_end)}</span>
                  </td>
                  {hasRentedInfo && <td><NextLease next={u.next} none={<span className="avp-muted">—</span>} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default AvailabilityPopover;
